import { CardHolderNameValidator } from '../../app/common/validator/cardHolderNameFieldValidator';
import { CardHolderNameValidationMessages } from '../../app/common/validator/cardHolderNameValidationMessages';
import { addRatEvent } from '../../utils/ratClickEvents';
import { PAYMENT_FORM_FIELD_IDS } from './constants';

interface PaymentMethodFormValues {
    ownerName: string;
    expireMonth?: string;
    expireYear?: string;
}

interface FormErrors {
    [key: string]: string | undefined;
}

export class PaymentMethodRatEventTracker {
    private cardHolderNameValidator: CardHolderNameValidator;

    constructor() {
        this.cardHolderNameValidator = new CardHolderNameValidator();
    }

    public trackValidationErrors(
        values: PaymentMethodFormValues,
        errors: FormErrors,
        isEditing: boolean = false,
    ): void {
        if (!errors || Object.keys(errors).length === 0) {
            return;
        }
        try {
            this.trackCardNumberErrors(errors, isEditing);
            this.trackCardHolderNameErrors(values.ownerName, errors, isEditing);
            this.trackExpiryDateErrors(errors, isEditing);
        } catch (error) {
            console.error('Failed to track validation errors:', error);
        }
    }

    private trackCardNumberErrors(errors: FormErrors, isEditing: boolean = false): void {
        if (isEditing) {
            return;
        }

        const hasCardNumberError = errors[PAYMENT_FORM_FIELD_IDS.PAYVAULT_CARD_NUMBER];

        if (hasCardNumberError) {
            addRatEvent('payment_method', 'add_card_number_invalid');
        }
    }

    private trackCardHolderNameErrors(
        ownerName: string,
        errors: FormErrors,
        isEditing: boolean = false,
    ): void {
        if (!errors[PAYMENT_FORM_FIELD_IDS.OWNER_NAME]) {
            return;
        }

        if (!ownerName || typeof ownerName !== 'string' || !ownerName.trim()) {
            if (isEditing) {
                addRatEvent('payment_method', 'edit_card_name_blank');
            } else {
                addRatEvent('payment_method', 'add_card_name_blank');
            }
            return;
        }

        const validationResult = this.cardHolderNameValidator.validate(ownerName);

        if (validationResult === null) {
            return;
        }

        const { RULE_NAMES } = CardHolderNameValidationMessages;
        const eventPrefix = isEditing ? 'edit_card' : 'add_card';

        const ruleToEventSuffixMap: Record<string, string> = {
            [RULE_NAMES.INVALID_FORMAT]: 'name_invalid_chars_kanji',
            [RULE_NAMES.INVALID_SYMBOLS]: 'name_invalid_chars',
            [RULE_NAMES.PAN_DETECTED]: 'name_pan_error',
        };

        const eventSuffix = ruleToEventSuffixMap[validationResult.rule];
        if (eventSuffix) {
            addRatEvent('payment_method', `${eventPrefix}_${eventSuffix}` as any);
        }
    }

    private trackExpiryDateErrors(errors: FormErrors, isEditing: boolean = false): void {
        const hasExpiryError =
            errors[PAYMENT_FORM_FIELD_IDS.EXPIRY_DATE] ||
            errors[PAYMENT_FORM_FIELD_IDS.PAYVAULT_EXPIRY_DATE] ||
            errors[PAYMENT_FORM_FIELD_IDS.EXPIRE_MONTH] ||
            errors[PAYMENT_FORM_FIELD_IDS.EXPIRE_YEAR];

        if (hasExpiryError) {
            const eventName = isEditing
                ? 'edit_card_expiry_date_error'
                : 'add_card_expiry_date_error';
            addRatEvent('payment_method', eventName);
        }
    }
}
